import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X, Linkedin, Facebook } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const navLinks = [
  { to: "/find-experience", label: "Find an Advisor", testId: "nav-find-experience-link" },
  { to: "/share-experience", label: "Become an Advisor", testId: "nav-share-experience-link" },
  { to: "/about", label: "About", testId: "nav-about-link" },
];

const GRAIN =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.55'/></svg>\")";

const GrainOverlay = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none fixed inset-0 z-[100] opacity-[0.035] mix-blend-overlay"
    style={{ backgroundImage: GRAIN }}
  />
);

export const PrimaryButton = ({ to, children, testId, className = "" }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`inline-block bg-primary text-primary-foreground text-lg font-semibold px-8 py-4 rounded-full text-center transition-[background-color,transform] duration-300 hover:bg-[#15325a] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${className}`}
  >
    {children}
  </Link>
);

export const GoldButton = ({ to, children, testId, className = "" }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`inline-block bg-secondary text-secondary-foreground text-lg font-semibold px-8 py-4 rounded-full text-center transition-[background-color,transform] duration-300 hover:bg-[#caa95c] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${className}`}
  >
    {children}
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="bg-background/90 backdrop-blur-md border-b border-border sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" className="shrink-0 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="One More Shift — home">
          <BrandLogo variant="light" className="h-12 md:h-14 w-auto" />
        </Link>
        <nav className="hidden xl:flex items-center gap-7" aria-label="Primary">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `text-base font-semibold rounded-sm px-1 transition-colors duration-200 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  isActive ? "text-accent underline underline-offset-8" : "text-foreground"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden xl:flex items-center gap-3 shrink-0">
          <Link
            to="/find-experience"
            data-testid="header-find-experience-button"
            className="bg-primary text-primary-foreground text-base font-semibold px-6 py-3 rounded-full transition-[background-color,transform] duration-300 hover:bg-[#15325a] hover:-translate-y-0.5"
          >
            Find an Advisor
          </Link>
          <Link
            to="/share-experience"
            data-testid="header-become-advisor-button"
            className="border-2 border-secondary text-foreground text-base font-semibold px-6 py-3 rounded-full transition-[background-color,color,transform] duration-300 hover:bg-secondary hover:text-secondary-foreground hover:-translate-y-0.5"
          >
            Become an Advisor
          </Link>
        </div>
        <button
          data-testid="mobile-menu-button"
          className="xl:hidden p-2 text-primary"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X size={32} /> : <Menu size={32} />}
        </button>
      </div>
      {open && (
        <nav className="xl:hidden border-t border-border bg-background px-6 py-4 flex flex-col gap-4" aria-label="Mobile">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-${l.testId}`}
              onClick={() => setOpen(false)}
              className="text-lg font-semibold text-foreground py-1"
            >
              {l.label}
            </NavLink>
          ))}
          <div className="flex flex-col gap-3 pt-2">
            <Link
              to="/find-experience"
              data-testid="mobile-find-experience-button"
              onClick={() => setOpen(false)}
              className="bg-primary text-primary-foreground text-lg font-semibold px-5 py-3 rounded-full text-center"
            >
              Find an Advisor
            </Link>
            <Link
              to="/share-experience"
              data-testid="mobile-become-advisor-button"
              onClick={() => setOpen(false)}
              className="border-2 border-secondary text-lg font-semibold px-5 py-3 rounded-full text-center"
            >
              Become an Advisor
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="bg-primary text-primary-foreground mt-0" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 py-14 grid gap-10 md:grid-cols-3">
      <div>
        <div data-testid="footer-brand">
          <BrandLogo variant="reversed" className="h-16 w-auto" />
        </div>
        <p className="mt-4 text-white/80">Initially serving Pearland, Friendswood, Alvin, League City, Webster, Dickinson and the Clear Lake area</p>
        <p className="mt-1 text-white/80">JoinOneMoreShift.com</p>
      </div>
      <nav aria-label="Footer" className="flex flex-col gap-3">
        <p className="font-semibold text-lg text-secondary">Explore</p>
        <Link to="/find-experience" data-testid="footer-find-experience-link" className="text-white/90 hover:text-white text-lg">I Need Experience</Link>
        <Link to="/share-experience" data-testid="footer-share-experience-link" className="text-white/90 hover:text-white text-lg">I Have Experience</Link>
        <Link to="/about" data-testid="footer-about-link" className="text-white/90 hover:text-white text-lg">About</Link>
        <Link to="/faq" data-testid="footer-faq-link" className="text-white/90 hover:text-white text-lg">FAQ</Link>
        <Link to="/privacy" data-testid="footer-privacy-link" className="text-white/90 hover:text-white text-lg">Privacy</Link>
        <Link to="/terms" data-testid="footer-terms-link" className="text-white/90 hover:text-white text-lg">Terms</Link>
      </nav>
      <div>
        <p className="font-semibold text-lg text-secondary">Connect With Us</p>
        <div className="mt-3 flex flex-col gap-3">
          <span data-testid="footer-linkedin-placeholder" className="inline-flex items-center gap-3 text-white/70 text-lg" aria-disabled="true">
            <Linkedin size={24} aria-hidden="true" /> LinkedIn — coming soon
          </span>
          <span data-testid="footer-facebook-placeholder" className="inline-flex items-center gap-3 text-white/70 text-lg" aria-disabled="true">
            <Facebook size={24} aria-hidden="true" /> Facebook — coming soon
          </span>
        </div>
        <p className="mt-6 text-white/70 text-base">
          Some engagements may require licenses, certifications, background checks or other qualifications, depending on the work involved.
        </p>
      </div>
    </div>
    <div className="border-t border-white/20">
      <p className="max-w-7xl mx-auto px-6 py-5 text-white/70 text-base">
        &copy; {new Date().getFullYear()} One More Shift. Now building our founding local advisor network.
      </p>
    </div>
  </footer>
);

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col">
      <GrainOverlay />
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
